'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onBlogCreateSuccess", "onBlogCreateFail", "onCancel"];
import PropTypes from 'prop-types';
import I18n from 'I18n';
import { getIsEnterprise } from 'ContentData/helpers/blogCreationHelpers';
import { sanitizeSlugInput } from 'ContentUtils/helpers/SlugHelpers';
import { useState, useCallback, useMemo, useEffect } from 'react';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import H2 from 'UIComponents/elements/headings/H2';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentSupportedLanguageSelect from 'ContentComponents/language/ContentSupportedLanguageSelect';
import Routes from 'ContentUtils/Routes';
import UILink from 'UIComponents/link/UILink';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { getLibraryTrack } from '../../utils/usageTracking';
import { useBlogQuickCreateRequiredData } from '../../hooks/blogHooks';
import * as blogsClient from 'ContentData/api/Blogs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const noop = () => {};
const isCreateDisabled = ({
  name,
  language,
  hasMultiLangBlogAccess,
  metaDescription
}) => {
  const isLanguageRequirementSatisfied = !hasMultiLangBlogAccess || hasMultiLangBlogAccess && language;
  return !name || !isLanguageRequirementSatisfied || !metaDescription;
};
const BlogName = ({
  name,
  onNameChange
}) => {
  const handleNameChange = useCallback(({
    target: {
      value
    }
  }) => onNameChange(value), [onNameChange]);
  return /*#__PURE__*/_jsx(UIFormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ContentComponents.blogCreationModal.blogName"
    }),
    required: true,
    children: /*#__PURE__*/_jsx(UITextInput, {
      "data-test-id": "blog-modal-name-input",
      value: name,
      onChange: handleNameChange
    })
  });
};
const BlogLanguageSelector = ({
  language,
  hasMultiLangBlogAccess,
  onLanguageChange
}) => {
  const handleLanguageChange = useCallback(({
    target: {
      value
    }
  }) => onLanguageChange(value), [onLanguageChange]);
  if (!hasMultiLangBlogAccess) {
    return null;
  }
  return /*#__PURE__*/_jsx(UIFormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ContentComponents.blogCreationModal.blogLanguage"
    }),
    required: true,
    children: /*#__PURE__*/_jsx(ContentSupportedLanguageSelect, {
      value: language,
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ContentComponents.blogCreationModal.selectBlogLanguage"
      }),
      onChange: handleLanguageChange
    })
  });
};
const BlogMetaDescription = ({
  metaDescription,
  onMetaDescriptionChange
}) => {
  const handleMetaDescriptionChange = useCallback(({
    target: {
      value
    }
  }) => onMetaDescriptionChange(value), [onMetaDescriptionChange]);
  return /*#__PURE__*/_jsx(UIFormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ContentComponents.blogCreationModal.blogMetaDescription"
    }),
    required: true,
    children: /*#__PURE__*/_jsx(UITextInput, {
      "data-test-id": "blog-modal-meta-description-input",
      value: metaDescription,
      onChange: handleMetaDescriptionChange
    })
  });
};
const RequiredDataGuard = ({
  hasRequiredData,
  isRequiredDataLoading,
  children
}) => {
  if (isRequiredDataLoading) {
    return /*#__PURE__*/_jsx(UIDialogBody, {
      className: "m-y-3",
      children: /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true
      })
    });
  }
  if (!hasRequiredData) {
    return /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ContentComponents.blogCreationModal.requiredDataError"
      })
    });
  }
  return children;
};
const BlogCreationModal = ({
  onCancel,
  isBlogCreateLoading,
  onCreate,
  metaDescription,
  onMetaDescriptionChange,
  hasMultiLangBlogAccess,
  onLanguageChange,
  language,
  name,
  onNameChange,
  hasRequiredData,
  isRequiredDataLoading,
  modalProps
}) => {
  return /*#__PURE__*/_jsxs(UIModal, Object.assign({
    width: 700
  }, modalProps, {
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onCancel
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ContentComponents.blogCreationModal.createBlog"
        })
      })]
    }), /*#__PURE__*/_jsxs(RequiredDataGuard, {
      hasRequiredData: hasRequiredData,
      isRequiredDataLoading: isRequiredDataLoading,
      children: [/*#__PURE__*/_jsxs(UIDialogBody, {
        children: [/*#__PURE__*/_jsx(BlogName, {
          name: name,
          onNameChange: onNameChange
        }), /*#__PURE__*/_jsx(BlogLanguageSelector, {
          hasMultiLangBlogAccess: hasMultiLangBlogAccess,
          onLanguageChange: onLanguageChange,
          language: language
        }), /*#__PURE__*/_jsx(BlogMetaDescription, {
          metaDescription: metaDescription,
          onMetaDescriptionChange: onMetaDescriptionChange
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          "data-test-id": "blog-modal-create-button",
          use: "primary",
          onClick: onCreate,
          loading: isBlogCreateLoading,
          disabled: isCreateDisabled({
            name,
            language,
            hasMultiLangBlogAccess,
            metaDescription
          }),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ContentComponents.blogCreationModal.create"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: onCancel,
          disabled: isBlogCreateLoading,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ContentComponents.blogCreationModal.cancel"
          })
        }), /*#__PURE__*/_jsx(UILink, {
          href: Routes.siteImporter(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ContentComponents.blogCreationModal.import"
          })
        })]
      })]
    })]
  }));
};
const useCreateBlogAndBlogPost = (onSuccess, onFail) => {
  const [isLoading, setIsLoading] = useState(false);
  const create = useCallback((blogData, blogPostData) => {
    setIsLoading(true);
    blogsClient.createBlog(blogData).then(blog => blogsClient.createBlogPost(blog.id, blogPostData).then(blogPost => ({
      blog,
      blogPost
    }))).then(({
      blog,
      blogPost
    }) => {
      setIsLoading(false);
      onSuccess(blog, blogPost);
    }).catch(err => {
      setIsLoading(false);
      onFail(err);
    });
  }, [onFail, onSuccess]);
  return {
    isLoading,
    create
  };
};
const blogCreationErrorSwitch = __error => {
  return {
    message: I18n.text('ContentComponents.blogCreationModal.errors.default.message'),
    titleText: I18n.text('ContentComponents.blogCreationModal.errors.default.title'),
    type: 'danger'
  };
};
export const QuickBlogCreationModal = _ref => {
  let {
      onBlogCreateSuccess = noop,
      onBlogCreateFail = noop,
      onCancel
    } = _ref,
    modalProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isLoading: isRequiredDataLoading,
    requiredData: {
      domain,
      blogTemplate,
      blogListingTemplate,
      defaultLanguage,
      scopes
    }
  } = useBlogQuickCreateRequiredData();
  const hasRequiredData = Boolean(domain) && Boolean(blogListingTemplate) && Boolean(blogTemplate) && Boolean(defaultLanguage);
  const track = getLibraryTrack();
  const handleBlogCreateFail = useCallback(error => {
    onBlogCreateFail(error);
    FloatingAlertStore.addAlert(blogCreationErrorSwitch(error));
  }, [onBlogCreateFail]);
  const {
    isLoading: isBlogCreateLoading,
    create: blogCreate
  } = useCreateBlogAndBlogPost(onBlogCreateSuccess, handleBlogCreateFail);
  const [name, setName] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [overrideLanguage, seOverrideLanguage] = useState('');
  const language = overrideLanguage || defaultLanguage;
  const isEnterprise = useMemo(() => getIsEnterprise(scopes), [scopes]);
  const blog = useMemo(() => {
    const blogName = name.trim();
    return {
      name: blogName,
      domain,
      language,
      slug: sanitizeSlugInput(blogName),
      itemTemplatePath: blogTemplate,
      listingTemplatePath: blogListingTemplate,
      htmlTitle: blogName,
      description: metaDescription
    };
  }, [name, blogTemplate, blogListingTemplate, metaDescription, domain, language]);
  const blogPost = useMemo(() => {
    return {
      language,
      name: I18n.text('ContentComponents.blogCreationModal.defaultPostTitle')
    };
  }, [language]);
  const onClickCreate = useCallback(() => {
    blogCreate(blog, blogPost);
    track('blogQuickCreateModalClickedCreate');
  }, [blog, blogCreate, blogPost, track]);
  useEffect(() => {
    track('blogQuickCreateModalOpened');
  }, [track]);
  return /*#__PURE__*/_jsx(BlogCreationModal, {
    hasMultiLangBlogAccess: true,
    isEnterprise: isEnterprise,
    isRequiredDataLoading: isRequiredDataLoading,
    hasRequiredData: hasRequiredData,
    isBlogCreateLoading: isBlogCreateLoading,
    language: language,
    onLanguageChange: seOverrideLanguage,
    name: name,
    metaDescription: metaDescription,
    onNameChange: setName,
    onMetaDescriptionChange: setMetaDescription,
    onCancel: onCancel,
    onCreate: onClickCreate,
    shouldRenderImportLink: true,
    modalProps: modalProps
  });
};
QuickBlogCreationModal.propTypes = {
  onBlogCreateSuccess: PropTypes.func,
  onBlogCreateFail: PropTypes.func,
  onCancel: PropTypes.func
};